import { useTranslation } from 'react-i18next';

import type { OmniCampaign, TOmniIntegration } from '@feathr/blackbox';
import { EIntegrationTypes } from '@feathr/blackbox';
import useMetaIntegrationStatus from '@feathr/extender/hooks/useMetaIntegrationStatus';
import { useAccount, useFlags } from '@feathr/extender/state';

import type { TMetaCardState } from './ChannelsCard.utils';
import { getMetaCardState, getMetaLockedTooltip } from './ChannelsCard.utils';

/**
 * Return type for the useChannelsCard hook.
 */
export interface IUseChannelsCardResult {
  /** Whether Display Advertising is selected */
  displaySelected: boolean;
  /** Whether Meta Advertising is selected */
  metaSelected: boolean;
  /** Handler for Display card selection changes */
  handleDisplayChange: (checked: boolean) => void;
  /** Handler for Meta card selection changes */
  handleMetaChange: (checked: boolean) => void;
  /** Current state of the Meta card (normal, locked, or setup) */
  metaCardState: TMetaCardState;
  /** Tooltip text when Meta card is locked, undefined otherwise */
  metaLockedTooltip: string | undefined;
  /** Whether the Channels card should be shown (metaV2 flag enabled) */
  showChannelsCard: boolean;
}

/**
 * Custom hook that manages state and logic for the ChannelsCard component.
 *
 * Handles:
 * - Channel selection state (display/meta) backed by campaign.integrations
 * - Meta card state based on account tier and integration status
 * - Feature flag gating
 *
 * @param campaign - The OmniCampaign model to read/write channel selections
 * @returns Object containing selection state, handlers, and computed values
 */
export function useChannelsCard(campaign: OmniCampaign): IUseChannelsCardResult {
  const { t } = useTranslation();
  const account = useAccount();
  const { metaV2 = false } = useFlags();
  const { hasExistingIntegration } = useMetaIntegrationStatus();

  const integrations: TOmniIntegration[] = campaign.get('integrations') ?? [];
  const displaySelected = integrations.includes(EIntegrationTypes.Tradedesk);
  const metaSelected = integrations.includes(EIntegrationTypes.Facebook);

  const isPlatformLite = account.isPlatformLite;

  const metaCardState = getMetaCardState({
    isPlatformLite,
    hasExistingIntegration,
  });

  const metaLockedTooltip = metaCardState === 'locked' ? getMetaLockedTooltip(t) : undefined;

  /**
   * Handles Display card selection changes.
   * Updates campaign.integrations array.
   */
  function handleDisplayChange(checked: boolean): void {
    const updated = integrations.filter((i) => i !== EIntegrationTypes.Tradedesk);
    if (checked) {
      updated.push(EIntegrationTypes.Tradedesk);
    }
    campaign.set({ integrations: updated });
  }

  /**
   * Handles Meta card selection changes.
   * Only allows selection when Meta card is in 'normal' state.
   * Clears meta_settings when Meta is deselected.
   */
  function handleMetaChange(checked: boolean): void {
    if (metaCardState !== 'normal') {
      return;
    }
    const updated = integrations.filter((i) => i !== EIntegrationTypes.Facebook);
    if (checked) {
      updated.push(EIntegrationTypes.Facebook);
    } else {
      campaign.set({ meta_settings: undefined });
    }
    campaign.set({ integrations: updated });
  }

  return {
    displaySelected,
    metaSelected,
    handleDisplayChange,
    handleMetaChange,
    metaCardState,
    metaLockedTooltip,
    showChannelsCard: metaV2,
  };
}
